import React, { useEffect, useState } from 'react';
import useMeta from '../hooks/useMeta';
import { leadSourceMappingsApi } from '../services/api';
import toast from 'react-hot-toast';

const emptyForm = { originDomain: '', pageId: '', pageName: '', isActive: true };

// Admin-only: manages the domain -> Facebook Page de-para used to route leads
// imported from external landing pages (e.g. the Apure/n8n database) into the same
// page-based access control as native Meta Lead Ads leads. See
// backend/src/services/apureLeadsSyncService.js for how this table is consumed.
const LeadSourceMappings = () => {
  const { pages, fetchPages } = useMeta();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    setLoading(true);
    try {
      const res = await leadSourceMappingsApi.list();
      setMappings(res.data?.mappings || []);
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao carregar mapeamentos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    fetchPages().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const handlePageSelect = (e) => {
    const pageId = e.target.value;
    const page = pages.find(p => p.id === pageId);
    setForm(prev => ({ ...prev, pageId, pageName: page?.name || prev.pageName }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.originDomain || !form.pageId) {
      toast.error('Domínio e Página são obrigatórios');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await leadSourceMappingsApi.update(editingId, form);
        toast.success('Mapeamento atualizado');
      } else {
        await leadSourceMappingsApi.create(form);
        toast.success('Mapeamento criado');
      }
      resetForm();
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao salvar mapeamento');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (mapping) => {
    setEditingId(mapping.id);
    setForm({
      originDomain: mapping.originDomain,
      pageId: mapping.pageId,
      pageName: mapping.pageName || '',
      isActive: mapping.isActive,
    });
  };

  const handleToggleActive = async (mapping) => {
    try {
      await leadSourceMappingsApi.update(mapping.id, { isActive: !mapping.isActive });
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao atualizar');
    }
  };

  const handleDelete = async (mapping) => {
    try {
      await leadSourceMappingsApi.delete(mapping.id);
      toast.success('Mapeamento removido');
      if (editingId === mapping.id) resetForm();
      await load();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Falha ao remover');
    }
  };

  return (
    <div className="animate-fade-in pb-16">
      <div className="mb-12">
        <h1 className="font-headline font-extrabold text-4xl tracking-tight text-on-surface mb-2">Mapeamento de Domínios</h1>
        <p className="text-secondary font-body">
          Associe o domínio de uma landing page (ex: itajai.apuresaude.com.br) a uma Página do Facebook, para que leads
          importados dessa origem apareçam para os mesmos usuários que já têm acesso à página.
        </p>
      </div>

      <div className="bg-surface-container-low rounded-xl p-8 mb-8">
        <h2 className="font-headline font-bold text-lg text-on-surface mb-6">{editingId ? 'Editar Mapeamento' : 'Novo Mapeamento'}</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">Domínio de Origem</label>
            <input
              type="text"
              className="input w-full"
              placeholder="itajai.apuresaude.com.br"
              value={form.originDomain}
              onChange={e => setForm(prev => ({ ...prev, originDomain: e.target.value }))}
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-1 block">Página do Facebook</label>
            <select className="input w-full" value={form.pageId} onChange={handlePageSelect}>
              <option value="">Selecione...</option>
              {pages.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={saving} className="primary-pill-gradient text-white px-6 py-2 rounded-full text-sm font-semibold hover:opacity-90 transition-all disabled:opacity-50 flex-1">
              {saving ? 'Salvando...' : editingId ? 'Salvar' : 'Adicionar'}
            </button>
            {editingId && (
              <button type="button" onClick={resetForm} className="px-4 py-2 rounded-full border border-outline-variant text-sm font-semibold hover:bg-white transition-all">
                Cancelar
              </button>
            )}
          </div>
        </form>
        {pages.length === 0 && (
          <p className="text-xs text-amber-600 mt-3">Nenhuma página carregada — vá em "Contas Meta" ou sincronize as páginas primeiro.</p>
        )}
      </div>

      <div className="bg-surface-container-low rounded-xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-12 text-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-slate-500 text-sm">Carregando mapeamentos...</p>
          </div>
        ) : mappings.length === 0 ? (
          <div className="p-12 text-center">
            <span className="material-symbols-outlined text-5xl text-slate-300 mb-4 block">alt_route</span>
            <p className="font-headline font-bold text-xl text-slate-700 mb-2">Nenhum mapeamento cadastrado</p>
            <p className="text-slate-500 text-sm">Leads importados de domínios sem mapeamento ficam registrados no log de sincronização até serem mapeados aqui.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-surface-container-lowest border-b border-outline-variant">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Domínio</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Página</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                <th className="px-6 py-4 text-right text-xs font-bold uppercase tracking-wider text-slate-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant">
              {mappings.map((m) => (
                <tr key={m.id} className="bg-white hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 font-mono text-xs text-on-surface">{m.originDomain}</td>
                  <td className="px-6 py-4 text-slate-700">{m.pageName || <span className="font-mono text-xs text-secondary">{m.pageId}</span>}</td>
                  <td className="px-6 py-4">
                    <button onClick={() => handleToggleActive(m)}>
                      {m.isActive ? (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Ativo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-500">
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inativo
                        </span>
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4 text-right space-x-3">
                    <button onClick={() => handleEdit(m)} className="text-primary font-semibold text-xs hover:underline">Editar</button>
                    <button onClick={() => handleDelete(m)} className="text-red-600 font-semibold text-xs hover:underline">Remover</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default LeadSourceMappings;
